import { create } from 'zustand';
import { useSessionStore } from './session-store';

const ANONYMOUS_SESSION_KEY = 'anonymous-session';
const SAMPLE_DATA_KEY_PREFIX = 'aeterna_sample_data_enabled:';
const REAL_DATA_SEED_KEY_PREFIX = 'aeterna_has_real_data_seed:';
const LEGACY_REAL_PROFILE_SEED_KEY_PREFIX = 'aeterna_has_real_profile_seed:';

interface SampleDataState {
  userKey: string;
  enabled: boolean;
  hasRealDataSeed: boolean;
}

interface SampleDataActions {
  refreshFromSession: () => void;
  enable: () => void;
  disable: () => void;
  markRealDataSeed: () => void;
  clearRealDataSeed: () => void;
}

type SampleDataStore = SampleDataState & SampleDataActions;

function resolveUserKey(): string {
  return useSessionStore.getState().user?.id || ANONYMOUS_SESSION_KEY;
}

function sampleEnabledKey(userKey: string) {
  return `${SAMPLE_DATA_KEY_PREFIX}${userKey}`;
}

function realDataSeedKey(userKey: string) {
  return `${REAL_DATA_SEED_KEY_PREFIX}${userKey}`;
}

function legacyRealProfileSeedKey(userKey: string) {
  return `${LEGACY_REAL_PROFILE_SEED_KEY_PREFIX}${userKey}`;
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, enabled: boolean) {
  try {
    if (enabled) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // 内存态仍可继续当前会话
  }
}

function readRealDataSeed(userKey: string): boolean {
  return readFlag(realDataSeedKey(userKey)) || readFlag(legacyRealProfileSeedKey(userKey));
}

function writeRealDataSeed(userKey: string, enabled: boolean) {
  writeFlag(realDataSeedKey(userKey), enabled);
  writeFlag(legacyRealProfileSeedKey(userKey), false);
}

function loadState(userKey: string): SampleDataState {
  return {
    userKey,
    enabled: readFlag(sampleEnabledKey(userKey)),
    hasRealDataSeed: readRealDataSeed(userKey),
  };
}

export const useSampleDataStore = create<SampleDataStore>((set, get) => ({
  ...loadState(resolveUserKey()),

  refreshFromSession: () => {
    set(loadState(resolveUserKey()));
  },

  enable: () => {
    const userKey = resolveUserKey();
    const hasRealDataSeed = readRealDataSeed(userKey);
    const enabled = !hasRealDataSeed;
    writeFlag(sampleEnabledKey(userKey), enabled);
    set({ userKey, enabled, hasRealDataSeed });
  },

  disable: () => {
    const userKey = resolveUserKey();
    writeFlag(sampleEnabledKey(userKey), false);
    set({ userKey, enabled: false, hasRealDataSeed: readRealDataSeed(userKey) });
  },

  markRealDataSeed: () => {
    const userKey = resolveUserKey();
    writeRealDataSeed(userKey, true);
    writeFlag(sampleEnabledKey(userKey), false);
    set({ userKey, enabled: false, hasRealDataSeed: true });
  },

  clearRealDataSeed: () => {
    const userKey = resolveUserKey();
    writeRealDataSeed(userKey, false);
    set({
      userKey,
      enabled: get().userKey === userKey ? get().enabled : readFlag(sampleEnabledKey(userKey)),
      hasRealDataSeed: false,
    });
  },
}));

useSessionStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    useSampleDataStore.getState().refreshFromSession();
  }
});
